import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Card } from '../components/ui/card.js';
import {
  searchNotifications,
  markRead,
  markAllRead,
  type Notification,
} from '../features/notifications/api.js';
import { listActivity, type ActivityEntry } from '../features/activity/api.js';
import { HOTELS_QUERY_KEY, listHotels, type Hotel } from '../features/hotels/api.js';

type Tab = 'notifications' | 'activity';

const NOTIFICATION_KIND_LABEL: Record<string, string> = {
  mention: '@említés',
  approval_reminder: 'Jóváhagyás emlékeztető',
  approval_decision: 'Ügyfél döntés',
  client_view: 'Ügyfél megnyitotta',
  ai_job_failed: 'AI hiba',
  client_comment: 'Ügyfél üzenete',
};

const ACTION_LABEL: Record<string, string> = {
  'hotel.create': 'Hotel létrehozva',
  'hotel.update': 'Hotel frissítve',
  'hotel.delete': 'Hotel törölve',
  'plan.create': 'Terv létrehozva',
  'plan.update': 'Terv frissítve',
  'plan.delete': 'Terv törölve',
  'post.create': 'Poszt létrehozva',
  'post.update': 'Poszt módosítva',
  'post.delete': 'Poszt törölve',
  'post.apply_action': 'Poszt státusza változott',
  'approval_batch.create': 'Jóváhagyási csomag elküldve',
  'approval_batch.revoke': 'Jóváhagyási csomag visszavonva',
};

const TARGET_LABEL: Record<string, string> = {
  hotels: 'hotel',
  monthly_plans: 'terv',
  posts: 'poszt',
  approval_batches: 'jóváhagyás',
  organizations: 'szervezet',
};

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('hu-HU', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function InboxPage() {
  const [tab, setTab] = useState<Tab>('notifications');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [hotelFilter, setHotelFilter] = useState('');
  const [targetType, setTargetType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const qc = useQueryClient();

  // Shared key from features/hotels/api — every /hotels reader across the app
  // goes through HOTELS_QUERY_KEY, so React Query's dedup is the default.
  const hotelsQ = useQuery<Hotel[]>({
    queryKey: HOTELS_QUERY_KEY,
    queryFn: listHotels,
    staleTime: 60_000,
  });

  const notificationsQ = useQuery({
    queryKey: ['inbox-notifications', query, kind, unreadOnly, from, to],
    queryFn: () =>
      searchNotifications({
        limit: 200,
        search: query || null,
        kind: kind || null,
        unreadOnly,
        from: from || null,
        to: to ? `${to}T23:59:59` : null,
      }),
    enabled: tab === 'notifications',
  });

  const activityQ = useQuery({
    queryKey: ['inbox-activity', query, hotelFilter, targetType, from, to],
    queryFn: () =>
      listActivity({
        limit: 200,
        search: query || null,
        hotelId: hotelFilter || null,
        targetType: targetType || null,
        from: from || null,
        to: to ? `${to}T23:59:59` : null,
      }),
    enabled: tab === 'activity',
  });

  const readMut = useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inbox-notifications'] });
      void qc.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const readAllMut = useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inbox-notifications'] });
      void qc.invalidateQueries({ queryKey: ['notifications-count'] });
    },
  });

  const notifications = notificationsQ.data ?? [];
  const activity = activityQ.data ?? [];

  const kindOptions = useMemo(() => {
    const set = new Set<string>(Object.keys(NOTIFICATION_KIND_LABEL));
    notifications.forEach((n) => set.add(n.kind));
    return Array.from(set).sort();
  }, [notifications]);

  const targetOptions = useMemo(() => {
    const set = new Set<string>(Object.keys(TARGET_LABEL));
    activity.forEach((a) => {
      if (a.targetType) set.add(a.targetType);
    });
    return Array.from(set).sort();
  }, [activity]);

  function clearFilters() {
    setQuery('');
    setKind('');
    setUnreadOnly(false);
    setHotelFilter('');
    setTargetType('');
    setFrom('');
    setTo('');
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div>
        <h1 className="text-h1 font-semibold text-[var(--ink-700)]">Értesítések és műveletek</h1>
        <p className="mt-1 text-meta text-[var(--fg-faint)]">
          Visszaolvasható és kereshető értesítési és audit napló — független a felső kereső mezőtől.
        </p>
      </div>

      <div className="flex gap-2 border-b border-[var(--app-divider)]">
        <TabButton
          active={tab === 'notifications'}
          label="Értesítések"
          onClick={() => setTab('notifications')}
        />
        <TabButton
          active={tab === 'activity'}
          label="Műveletek"
          onClick={() => setTab('activity')}
        />
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="md:col-span-2">
            <div className="mb-1 text-meta font-medium uppercase tracking-wide text-[var(--fg-faint)]">
              Keresés
            </div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                tab === 'notifications' ? 'Cím vagy szöveg…' : 'Művelet, e-mail vagy hotel neve…'
              }
              className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)] placeholder:text-[var(--fg-faint)] focus:border-[var(--brand-500)] focus:outline-none"
            />
          </label>
          <label>
            <div className="mb-1 text-meta font-medium uppercase tracking-wide text-[var(--fg-faint)]">
              -tól
            </div>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)] focus:border-[var(--brand-500)] focus:outline-none"
            />
          </label>
          <label>
            <div className="mb-1 text-meta font-medium uppercase tracking-wide text-[var(--fg-faint)]">
              -ig
            </div>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)] focus:border-[var(--brand-500)] focus:outline-none"
            />
          </label>

          {tab === 'notifications' ? (
            <>
              <label>
                <div className="mb-1 text-meta font-medium uppercase tracking-wide text-[var(--fg-faint)]">
                  Típus
                </div>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)] focus:border-[var(--brand-500)] focus:outline-none"
                >
                  <option value="">Mind</option>
                  {kindOptions.map((k) => (
                    <option key={k} value={k}>
                      {NOTIFICATION_KIND_LABEL[k] ?? k}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-end gap-2 pb-1.5">
                <input
                  id="unread-only"
                  type="checkbox"
                  checked={unreadOnly}
                  onChange={(e) => setUnreadOnly(e.target.checked)}
                />
                <span className="text-body text-[var(--ink-700)]">Csak olvasatlan</span>
              </label>
            </>
          ) : (
            <>
              <label>
                <div className="mb-1 text-meta font-medium uppercase tracking-wide text-[var(--fg-faint)]">
                  Hotel
                </div>
                <select
                  value={hotelFilter}
                  onChange={(e) => setHotelFilter(e.target.value)}
                  className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)] focus:border-[var(--brand-500)] focus:outline-none"
                >
                  <option value="">Mind</option>
                  {(hotelsQ.data ?? []).map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <div className="mb-1 text-meta font-medium uppercase tracking-wide text-[var(--fg-faint)]">
                  Cél
                </div>
                <select
                  value={targetType}
                  onChange={(e) => setTargetType(e.target.value)}
                  className="w-full rounded-md border border-[var(--app-divider)] bg-transparent px-3 py-1.5 text-body text-[var(--ink-700)] focus:border-[var(--brand-500)] focus:outline-none"
                >
                  <option value="">Mind</option>
                  {targetOptions.map((t) => (
                    <option key={t} value={t}>
                      {TARGET_LABEL[t] ?? t}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={clearFilters}
            className="text-meta text-[var(--fg-faint)] hover:underline"
          >
            Szűrők törlése
          </button>
          {tab === 'notifications' && (
            <button
              type="button"
              onClick={() => readAllMut.mutate()}
              disabled={readAllMut.isPending}
              className="text-meta text-[var(--brand-700)] hover:underline disabled:opacity-50"
            >
              Mind olvasottnak jelölés
            </button>
          )}
        </div>
      </Card>

      {tab === 'notifications' ? (
        <NotificationsList
          loading={notificationsQ.isLoading}
          items={notifications}
          onMarkRead={(id) => readMut.mutate(id)}
        />
      ) : (
        <ActivityList loading={activityQ.isLoading} items={activity} />
      )}
    </div>
  );
}

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-body font-medium transition-colors ${
        active
          ? 'border-[var(--brand-500)] text-[var(--ink-700)]'
          : 'border-transparent text-[var(--fg-faint)] hover:text-[var(--ink-700)]'
      }`}
    >
      {label}
    </button>
  );
}

function NotificationsList({
  loading,
  items,
  onMarkRead,
}: {
  loading: boolean;
  items: Notification[];
  onMarkRead: (id: string) => void;
}) {
  if (loading) {
    return <Card className="p-4 text-body text-[var(--fg-faint)]">Értesítések betöltése…</Card>;
  }
  if (items.length === 0) {
    return <Card className="p-4 text-body text-[var(--fg-faint)]">Nincs találat.</Card>;
  }
  return (
    <Card className="divide-y divide-[var(--app-divider)] overflow-hidden">
      {items.map((n) => {
        const unread = !n.readAt;
        const kindLabel = NOTIFICATION_KIND_LABEL[n.kind] ?? n.kind;
        const body = (
          <div className="flex items-start gap-3 p-3">
            <div
              className={`mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                unread ? 'bg-[var(--brand-500)]' : 'bg-[var(--app-divider)]'
              }`}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="rounded-full bg-[var(--brand-50)] px-2 py-0.5 text-meta font-medium text-[var(--brand-700)]"
                  title={kindLabel}
                >
                  {kindLabel}
                </span>
                <span className="text-meta text-[var(--fg-faint)]">{fmt(n.createdAt)}</span>
              </div>
              <div className="mt-1 text-body font-medium text-[var(--ink-700)]">{n.title}</div>
              {n.body && <div className="mt-0.5 text-body text-[var(--fg-muted)]">{n.body}</div>}
            </div>
            <div className="flex flex-col items-end gap-1">
              {unread && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onMarkRead(n.id);
                  }}
                  className="text-meta text-[var(--brand-700)] hover:underline"
                  title="Olvasottnak jelöl"
                >
                  Olvasott
                </button>
              )}
            </div>
          </div>
        );
        return n.link ? (
          <Link
            key={n.id}
            to={n.link}
            onClick={() => unread && onMarkRead(n.id)}
            className="block hover:bg-[var(--app-divider)]/30"
          >
            {body}
          </Link>
        ) : (
          <div key={n.id}>{body}</div>
        );
      })}
    </Card>
  );
}

function ActivityList({ loading, items }: { loading: boolean; items: ActivityEntry[] }) {
  if (loading) {
    return <Card className="p-4 text-body text-[var(--fg-faint)]">Műveletek betöltése…</Card>;
  }
  if (items.length === 0) {
    return <Card className="p-4 text-body text-[var(--fg-faint)]">Nincs találat.</Card>;
  }
  return (
    <Card className="divide-y divide-[var(--app-divider)] overflow-hidden">
      {items.map((a) => {
        const actionLabel = ACTION_LABEL[a.action] ?? a.action;
        const targetLabel = a.targetType ? (TARGET_LABEL[a.targetType] ?? a.targetType) : null;
        return (
          <div key={a.id} className="flex items-start gap-3 p-3">
            <div
              className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand-500)]"
              aria-hidden="true"
              title={actionLabel}
            />
            <div className="min-w-0 flex-1">
              <div className="text-body text-[var(--ink-700)]">{actionLabel}</div>
              <div className="mt-0.5 text-meta text-[var(--fg-faint)]">
                {fmt(a.occurredAt)}
                {a.actorEmail && ` · ${a.actorEmail}`}
                {a.hotelName && ` · ${a.hotelName}`}
                {targetLabel && ` · ${targetLabel}`}
              </div>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

export const Route = createFileRoute('/app/inbox')({
  component: InboxPage,
});
